import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Row, Col, Alert, Spinner, InputGroup
} from 'react-bootstrap';
import { FaEdit, FaTrashAlt, FaPlus, FaMinus, FaSearch, FaEuroSign } from 'react-icons/fa';
import { pricingAPI } from '../../api/apiClient';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';

function PricingPostalCode() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  // const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedId, setHighlightedId] = useState(null);
  const [form, setForm] = useState({
    postal_codes: [''],
    price: '',
  });
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  const fetchData = async (page = 1, search = searchTerm) => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page,
        per_page: pagination.per_page,
        search: searchTerm,
      }

      const response = await pricingAPI.postalCode.index(params);

      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_fetch_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_fetch_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchData(1, searchTerm);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    fetchData();
  }, []);

  const openForm = (entry = null) => {
    if (entry) {
      const postalCodesArray = Array.isArray(entry.postal_codes) 
        ? entry.postal_codes 
        : (entry.postal_codes || '').split(',').map(code => code.trim()).filter(code => code !== '');
      setForm({
        postal_codes: postalCodesArray.length > 0 ? postalCodesArray : [''],
        price: entry.price || ''
      });
      setEditData(entry);
      setHighlightedId(entry.id);
    } else {
      setForm({
        postal_codes: [''],
        price: ''
      });
      setEditData(null);
      setHighlightedId(null);
    }
    setShowForm(true);
  };

  const addPostalCode = () => {
    setForm(prev => ({
      ...prev,
      postal_codes: [...prev.postal_codes, '']
    }));
  };

  const removePostalCode = (index) => {
    if (form.postal_codes.length > 1) {
      setForm(prev => ({
        ...prev,
        postal_codes: prev.postal_codes.filter((_, i) => i !== index)
      }));
    }
  };

  const updatePostalCode = (index, value) => {
    setForm(prev => ({
      ...prev,
      postal_codes: prev.postal_codes.map((code, i) => i === index ? value : code)
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      // const formData = {
      //   ...form,
      //   postal_codes: form.postal_codes.split(',').map(code => code.trim()).filter(code => code !== '')
      // };

      let postalCodesArray = [];
    
      if (Array.isArray(form.postal_codes)) {
        postalCodesArray = form.postal_codes.filter(code => code && code.trim() !== '');
      } else if (typeof form.postal_codes === 'string') {
        postalCodesArray = form.postal_codes
          .split(',')
          .map(code => code.trim())
          .filter(code => code !== '');
      } else {
        postalCodesArray = [];
      }

      if (postalCodesArray.length === 0) {
        postalCodesArray = [''];
      }

      const formData = {
        ...form,
        postal_codes: postalCodesArray
      };

      const fd = new FormData();
      
      // Append postal codes as array
      // postalCodesArray.forEach((code, index) => {
      //   fd.append(`postal_codes[${index}]`, code);
      // });
      
      fd.append('price', form.price);

      let response;
      if (editData) {
        response = await pricingAPI.postalCode.update(editData.id, formData);
      } else {
        response = await pricingAPI.postalCode.store(formData);
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_saved_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowForm(false);
        fetchData(pagination.current_page);
        if (editData) {
          setHighlightedId(editData.id);
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_save_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_save_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error saving data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (id) => {
    setDeleteId(id);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await pricingAPI.postalCode.delete(deleteId);
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_deleted_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowDelete(false);
        fetchData(pagination.current_page);
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_delete_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_delete_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error deleting data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="dashboard-title"><FaEuroSign /> {t('pricing_postal_codes')}</h3>
        <Button className='add-btn' onClick={() => openForm()}>
          <FaPlus className="me-1" /> {t('Neu_hinzufügen')}
        </Button>
      </div>

      {/* Search Bar */}
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={t('search_group_postal')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button 
                variant="outline-secondary" 
                onClick={clearSearch}
                title={t("reset_search")}
              >
                ✕
              </Button>
            )}
          </InputGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : data.length === 0 ? (
        <Alert variant="info">
          {searchTerm ? 
            `Keine Ergebnisse gefunden für "${searchTerm}"` : 
            t('no_data_available')
          }
        </Alert>
      ) : (
        <div className="table-container shadow-sm rounded">
          <div className='table-responsive'>
            <Table bordered hover className="custom-table mb-0">
              <thead className="table-dark text-center">
                <tr>
                  <th>#</th>
                  <th>{t('postal_code')}</th>
                  <th>{t('price')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((entry, idx) => (
                  <tr key={entry.id} className={`text-center ${highlightedId === entry.id ? 'row-highlighted' : ''}`}>
                    <td>{(pagination.current_page - 1) * pagination.per_page + idx + 1}</td>
                    <td>
                      <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {Array.isArray(entry.postal_codes) ? 
                          entry.postal_codes.map(code => 
                            searchTerm ? (
                              <span 
                                key={code}
                                dangerouslySetInnerHTML={{
                                  __html: code.toString().replace(
                                    new RegExp(searchTerm, 'gi'), 
                                    match => `<mark class="bg-warning">${match}</mark>`
                                  )
                                }} 
                              />
                            ) : code
                          ).reduce((prev, curr) => [prev, ', ', curr]) 
                          : entry.postal_codes
                        }
                      </div>
                    </td>
                    <td>
                      {searchTerm ? (
                        <span 
                          dangerouslySetInnerHTML={{
                            __html: entry.price?.toString().replace(
                              new RegExp(searchTerm, 'gi'), 
                              match => `<mark class="bg-warning">${match}</mark>`
                            )
                          }} 
                        />
                      ) : (
                        entry.price
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        className="me-2 add-btn"
                        onClick={() => openForm(entry)}
                        title="Bearbeiten"
                        disabled={loading}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openDelete(entry.id)}
                        title={t("Delete")}
                        disabled={loading}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      
      {!loading && data.length > 0 && pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={fetchData}
          disabled={loading}
        />
      )}

      {/* Add/Edit Modal */}
      <Modal 
        show={showForm} 
        onHide={() => {
          setShowForm(false);
          if (editData) {
            setHighlightedId(editData.id);
          }
        }} 
        size="xl" 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editData ? t('Edit') : t('Add')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t("postal_code")}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    type="text"
                    value={form.postal_codes}
                    onChange={e => setForm(prev => ({ ...prev, postal_codes: e.target.value }))}
                    placeholder="50667, 50668, 50670"
                  />
                  <Form.Text className="text-muted">
                    {t("postal_code_note")}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("price")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.price}
                    onChange={e => setForm(prev => ({ ...prev, price: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={loading}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? t('Saving...') : t('save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirm_delete_group")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t('Deleting...') : t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .postal-codes-container {
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 1rem;
          background-color: #f8f9fa;
        }
        
        mark {
          background-color: #ffc107;
          padding: 0.1rem 0.2rem;
          border-radius: 0.2rem;
        }
      `}</style>
    </div>
  );
}

export default PricingPostalCode;